import { useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  describeCaptureStatusChangeError,
  updateCaptureStatus,
} from '../../data/writers/update-capture-status';
import { reportError } from '../../lib/monitoring';
import type { CaptureStatus } from '../../data/database.types';
import { CAPTURE_STATUS_LABEL, CAPTURE_STATUS_ORDER } from './capture-status';
import './captures.css';
import { errorCodeOf } from '../../lib/error-classification';

/**
 * CHANGING A CAPTURE'S STATUS — M6 / S06-02.
 *
 * **One tap. That is the entire interaction.** No confirmation, no reason, no
 * note, no form, no separate screen. Brandon is on a roof with one glove off;
 * every extra tap is work this product exists to remove, and all three
 * statuses are freely reversible so a mis-tap costs one more tap.
 *
 * The control is the SAME three-button group used while creating a Capture —
 * same labels, same classes, same glyph-and-word rule (colour is never the
 * only signal). Reused rather than reinvented so the two screens cannot drift
 * into meaning different things by the same words.
 *
 * WHAT HAPPENS UNDERNEATH, so the tap can stay a tap:
 *
 *   The writer is given the status the SCREEN believed the Capture was in.
 *   If the server disagrees, the change is refused rather than overwriting
 *   whatever happened in between — and Brandon is told plainly, with the real
 *   status shown, instead of being asked to try again against a stale view.
 *
 *   Tapping the status it already has does nothing at all: no request, no
 *   write, no Work Log event. An event recording a change that never happened
 *   would be a lie in the history.
 *
 *   On success BOTH the Capture and its History are reloaded, so the new
 *   entry appears immediately without reopening the screen. This is a new
 *   S06-02 mutation refreshing its own surfaces — no global store, no
 *   cross-feature plumbing.
 *
 * **NOTHING HERE TOUCHES THE PROJECT.** There is no Project argument and no
 * Project write anywhere in this path.
 */
export function CaptureStatusControl({
  captureId,
  status,
  onChanged,
}: {
  captureId: string;
  /** Server truth as currently loaded — also the concurrency guard's basis. */
  status: CaptureStatus;
  /** Reload Capture + History. Awaited, so the buttons stay disabled until truth is back. */
  onChanged: () => Promise<void>;
}) {
  const client = getSupabaseClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (next: CaptureStatus): Promise<void> => {
    // ALREADY THERE — no request at all. The server would answer the same way
    // (it refuses to write an event for a no-op), but not asking is faster and
    // keeps a pointless round trip off a weak site connection.
    if (next === status || busy || !client) return;

    setBusy(true);
    setError(null);
    try {
      await updateCaptureStatus(client, captureId, status, next);
      // Truth first, then the screen: the reload is what updates the chip, so
      // nothing is shown as changed until the server says it changed.
      await onChanged();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      reportError(caught, 'capture.updateStatus');
      setError(describeCaptureStatusChangeError(message, errorCodeOf(caught) ?? undefined));
      // A STALE REFUSAL STILL RELOADS. The screen is out of date by
      // definition, so showing the real status is the useful half of the
      // answer — the sentence explains why it is not what he tapped.
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="capture-detail-status" aria-labelledby="capture-status-heading">
      <h2 id="capture-status-heading" className="projects-group-heading">
        STATUS
      </h2>
      <div className="capture-status-choices" role="group" aria-label="Capture status">
        {CAPTURE_STATUS_ORDER.map((option) => (
          <button
            key={option}
            type="button"
            className={`capture-status-choice capture-status-choice--${option}${
              option === status ? ' capture-status-choice--current' : ''
            }`}
            aria-pressed={option === status}
            disabled={busy}
            onClick={() => {
              void change(option);
            }}
          >
            {CAPTURE_STATUS_LABEL[option]}
          </button>
        ))}
      </div>

      {error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
